import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  IconButton,
  Divider,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { request } from "../../config/request.ts";
import { formatTime } from "../../config/formatters.ts";
import { toast } from "react-toastify";

interface WashData {
  id: number;
  vehicle?: {
    regNo: string;
    vehicleDetails?: string;
    customer?: { name: string; phone: string };
  };
  masterServices?: { serviceName: string }[];
  actualAmount?: number | null;
  washStatus?: string;
  paymentStatus?: string;
  vehicleArrivedDate?: string;
  vehicleArrivedTime?: string;
  remarks?: string;
}

interface CompleteWashDialogProps {
  open: boolean;
  onClose: () => void;
  wash: WashData | null;
  onSuccess: () => void;
}

const statusColors: Record<string, { bg: string; color: string }> = {
  Completed: { bg: "rgba(16, 185, 129, 0.1)", color: "#10b981" },
  Pending: { bg: "rgba(245, 158, 11, 0.1)", color: "#f59e0b" },
  "In Progress": { bg: "rgba(37, 99, 235, 0.1)", color: "#2563eb" },
  Cancelled: { bg: "rgba(239, 68, 68, 0.1)", color: "#ef4444" },
};

export default function CompleteWashDialog({
  open,
  onClose,
  wash,
  onSuccess,
}: CompleteWashDialogProps) {
  const [submitting, setSubmitting] = useState(false);

  const handleComplete = async () => {
    if (!wash) return;
    setSubmitting(true);
    try {
      await request.post("/api/completeWash", { washId: wash.id });
      toast.success("Wash marked as completed!");
      onSuccess();
      onClose();
    } catch {
      toast.error("Failed to complete wash");
    } finally {
      setSubmitting(false);
    }
  };

  if (!wash) return null;

  const statusScheme = statusColors[wash.washStatus || "Pending"] || statusColors.Pending;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2.5, overflow: "hidden" } }}
    >
      <Box sx={{ background: "linear-gradient(135deg, #10b981, #059669)", px: 2.5, py: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
        <CheckCircleIcon sx={{ fontSize: 22, color: "#fff" }} />
        <Box sx={{ flex: 1, color: "#fff", fontWeight: 700, fontSize: "0.95rem" }}>
          Complete Wash
        </Box>
        <IconButton onClick={onClose} sx={{ color: "rgba(255,255,255,0.8)", p: 0.5 }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <DialogContent sx={{ p: 2.5 }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
            <Info label="Reg No" value={wash.vehicle?.regNo || "-"} />
            <Info label="Arrival" value={`${wash.vehicleArrivedDate || "-"} ${wash.vehicleArrivedTime ? formatTime(wash.vehicleArrivedTime) : ""}`} />
          </Box>

          <Divider sx={{ borderStyle: "dashed", opacity: 0.5 }} />

          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
            <Info label="Customer" value={wash.vehicle?.customer?.name || "-"} />
            <Info label="Phone" value={wash.vehicle?.customer?.phone || "-"} />
          </Box>

          <Divider sx={{ borderStyle: "dashed", opacity: 0.5 }} />

          <Info
            label="Services"
            value={wash.masterServices?.map((s) => s.serviceName).join(", ") || "-"}
          />
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
            <Info
              label="Amount"
              value={wash.actualAmount != null ? `₹${Number(wash.actualAmount).toLocaleString()}` : "-"}
            />
            <Box>
              <Box sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.3, mb: 0.3 }}>
                Status
              </Box>
              <Chip
                label={wash.washStatus || "Pending"}
                size="small"
                sx={{ background: statusScheme.bg, color: statusScheme.color, fontWeight: 700, fontSize: "0.7rem", height: 22 }}
              />
            </Box>
          </Box>

          {wash.remarks && (
            <>
              <Divider sx={{ borderStyle: "dashed", opacity: 0.5 }} />
              <Info label="Remarks" value={wash.remarks} />
            </>
          )}
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          size="small"
          sx={{ borderColor: "rgba(0,0,0,0.12)", color: "#374151", textTransform: "none", fontWeight: 600, borderRadius: 1.5, px: 2 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleComplete}
          variant="contained"
          size="small"
          disabled={submitting}
          startIcon={<CheckCircleIcon />}
          sx={{
            background: "linear-gradient(135deg, #10b981, #059669)",
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 1.5,
            px: 2,
            "&:hover": { background: "linear-gradient(135deg, #059669, #047857)" },
          }}
        >
          {submitting ? "Completing..." : "Mark as Completed"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Box sx={{ fontSize: "0.65rem", fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: 0.3, mb: 0.15 }}>
        {label}
      </Box>
      <Box sx={{ fontSize: "0.82rem", fontWeight: 600, color: "#1c1b1f", wordBreak: "break-word" }}>
        {value}
      </Box>
    </Box>
  );
}
